import * as THREE from 'three';
import { Entity } from './Entity.js';
import { HOUSE_COLORS, FLOWER_COLORS } from '../config.js';

/**
 * @typedef {Object} DoorstepInfo
 * @property {THREE.Mesh} object - The doorstep mesh
 * @property {THREE.Vector3} worldPosition - Doorstep position in world space
 */

/**
 * @typedef {Object} DebrisPiece
 * @property {THREE.Mesh} mesh
 * @property {'wall' | 'roof'} type
 * @property {THREE.Vector3} velocity
 * @property {THREE.Vector3} angularVelocity
 * @property {boolean} landed
 * @property {number=} settleFrames
 * @property {THREE.Vector3=} settleBasePosition
 * @property {THREE.Euler=} settleBaseRotation
 */

/**
 * @typedef {Object} DebrisObstacle
 * @property {THREE.Vector3} position
 * @property {number} collisionRadius
 */

/**
 * @typedef {Object} HouseOptions
 * @property {THREE.Vector3} [position] - Initial position
 * @property {number} [rotation] - Initial rotation in radians
 * @property {number} [color] - Override house wall color (hex)
 */

/**
 * A house entity with walls, roof, windows, door, doorstep, and garden with flowers.
 * Doorsteps can be pooped on for points.
 */
export class House extends Entity {
    /** @type {'normal' | 'burning' | 'scorched'} */
    #burnState = 'normal';

    /** @type {number} */
    #burnTimeRemaining = 0;

    /** @type {THREE.Mesh} */
    #doorstep = /** @type {THREE.Mesh} */ (/** @type {unknown} */ (null));

    /** @type {boolean} */
    #doorstepPooped = false;

    /** @type {THREE.Sprite|null} */
    #poopSprite = null;

    /** @type {THREE.Mesh | null} */
    #baseMesh = null;

    /** @type {THREE.Mesh | null} */
    #roofMesh = null;

    /** @type {THREE.Mesh | null} */
    #doorMesh = null;

    /** @type {THREE.Mesh[]} */
    #windowMeshes = [];

    /** @type {THREE.Mesh | null} */
    #gardenMesh = null;

    /** @type {THREE.Group[]} */
    #flowers = [];

    /** @type {DebrisPiece[]} */
    #debrisPieces = [];

    /** @type {THREE.Vector3[]} */
    #wallLandingPositions = [];

    /** @type {THREE.Vector3 | null} */
    #roofLandingPosition = null;

    /** @type {boolean} */
    #exploded = false;

    /** @type {THREE.Vector3} */
    #explosionDirection = new THREE.Vector3(1, 0, 0);

    /** @type {number} */
    static WIDTH = 10;

    /** @type {number} */
    static DEPTH = 8;

    /** @type {number} */
    static COLLISION_RADIUS = Math.sqrt((House.WIDTH / 2) ** 2 + (House.DEPTH / 2) ** 2);

    /**
     * @param {HouseOptions} [options]
     */
    constructor(options = {}) {
        super({
            position: options.position,
            rotation: options.rotation,
            collisionRadius: House.COLLISION_RADIUS,
            collidable: true,
        });

        const color = options.color ?? HOUSE_COLORS[Math.floor(Math.random() * HOUSE_COLORS.length)];
        this.#buildMesh(color);
    }

    /**
     * Get the doorstep mesh
     * @returns {THREE.Mesh}
     */
    get doorstep() {
        return this.#doorstep;
    }

    /**
     * Whether this house's doorstep has been pooped on
     * @returns {boolean}
     */
    get doorstepPooped() {
        return this.#doorstepPooped;
    }

    /**
     * Whether this house has ever been burned
     * @returns {boolean}
     */
    get burned() {
        return this.#burnState !== 'normal';
    }

    /**
     * Whether this house is actively burning
     * @returns {boolean}
     */
    get burning() {
        return this.#burnState === 'burning';
    }

    /**
     * Whether this house finished burning
     * @returns {boolean}
     */
    get scorched() {
        return this.#burnState === 'scorched';
    }

    /**
     * Whether this house has exploded into debris.
     * @returns {boolean}
     */
    get exploded() {
        return this.#exploded;
    }

    /**
     * Houses stop being blocking colliders once exploded debris is on the ground.
     * @returns {boolean}
     */
    get collidable() {
        return !this.#exploded;
    }

    /**
     * World positions where wall debris landed.
     * @returns {THREE.Vector3[]}
     */
    get wallLandingPositions() {
        return this.#wallLandingPositions.map(pos => pos.clone());
    }

    /**
     * World position where roof debris landed.
     * @returns {THREE.Vector3 | null}
     */
    get roofLandingPosition() {
        return this.#roofLandingPosition ? this.#roofLandingPosition.clone() : null;
    }

    /**
     * Get landed debris collision obstacles so other entities cannot walk through pieces.
     * @returns {DebrisObstacle[]}
     */
    getDebrisObstacles() {
        /** @type {DebrisObstacle[]} */
        const obstacles = [];

        this.#debrisPieces.forEach((piece) => {
            if (!piece.landed) {
                return;
            }

            const worldPos = new THREE.Vector3();
            piece.mesh.getWorldPosition(worldPos);
            obstacles.push({
                position: worldPos,
                collisionRadius: piece.type === 'roof' ? 3.2 : 1.9,
            });
        });

        return obstacles;
    }

    /**
     * Ignite this house (idempotent)
     * @returns {boolean}
     */
    burn() {
        if (this.#burnState !== 'normal') {
            return false;
        }

        this.#burnState = 'burning';
        this.#burnTimeRemaining = 10.0;

        this.mesh.traverse((child) => {
            if (!(child instanceof THREE.Mesh)) {
                return;
            }

            const material = child.material;
            /** @param {unknown} mat */
            const ignite = (mat) => {
                if (!(mat instanceof THREE.MeshStandardMaterial)) {
                    return;
                }

                mat.emissive.setHex(0xff5500);
                mat.emissiveIntensity = 0.26;
            };

            if (Array.isArray(material)) {
                material.forEach(ignite);
            } else {
                ignite(material);
            }
        });

        return true;
    }

    /**
     * Update burn progression and visual flicker
     * @param {number} delta
        * @param {House[]} [houses]
        * @param {import('./Tree.js').Tree[]} [trees]
     */
        update(delta, houses = [], trees = []) {
        if (this.#burnState === 'burning') {
            this.#burnTimeRemaining -= delta;
            const flicker = 0.16 + Math.abs(Math.sin(Date.now() * 0.018)) * 0.22;

            this.mesh.traverse((child) => {
                if (!(child instanceof THREE.Mesh)) {
                    return;
                }

                const material = child.material;
                /** @param {unknown} mat */
                const applyFlicker = (mat) => {
                    if (mat instanceof THREE.MeshStandardMaterial) {
                        mat.emissiveIntensity = flicker;
                    }
                };

                if (Array.isArray(material)) {
                    material.forEach(applyFlicker);
                } else {
                    applyFlicker(material);
                }
            });

            if (this.#burnTimeRemaining <= 0) {
                this.#explode();
            }
        }

        if (this.#debrisPieces.length > 0) {
            this.#updateDebrisPhysics(delta, houses, trees);
        }
    }

    #explode() {
        if (this.#exploded) {
            return;
        }

        this.#exploded = true;
        this.#burnState = 'scorched';
        this.#wallLandingPositions = [];
        this.#roofLandingPosition = null;

        const randomDir = new THREE.Vector3(Math.random() - 0.5, 0, Math.random() - 0.5);
        if (randomDir.lengthSq() < 0.0001) {
            randomDir.set(1, 0, 0);
        }
        this.#explosionDirection.copy(randomDir.normalize());

        if (this.#baseMesh) {
            this.#baseMesh.visible = false;
        }
        if (this.#roofMesh) {
            this.#roofMesh.visible = false;
        }
        if (this.#doorMesh) {
            this.#doorMesh.visible = false;
        }
        this.#windowMeshes.forEach((windowMesh) => {
            windowMesh.visible = false;
        });

        this.#blowOverFlowers();
        this.#spawnExplosionDebris();
    }

    #blowOverFlowers() {
        const localDirection = this.#explosionDirection.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), -this.rotation);

        this.#flowers.forEach((flower) => {
            const lean = 0.85 + Math.random() * 0.45;
            flower.rotation.x = -localDirection.z * lean;
            flower.rotation.z = localDirection.x * lean;
            flower.position.x += localDirection.x * (0.25 + Math.random() * 0.45);
            flower.position.z += localDirection.z * (0.25 + Math.random() * 0.45);
        });
    }

    #spawnExplosionDebris() {
        const wallMaterial = new THREE.MeshStandardMaterial({ color: 0x3f3f3f, roughness: 0.9 });
        const frameMaterial = new THREE.MeshStandardMaterial({ color: 0x1f1f1f, roughness: 0.8 });

        /**
         * @param {THREE.Vector3} localPos
         * @param {number} localYaw
         * @param {boolean} withDoor
         * @param {boolean} withWindow
         */
        const createWallPiece = (localPos, localYaw, withDoor, withWindow) => {
            const wall = new THREE.Mesh(new THREE.BoxGeometry(4.8, 3.6, 0.28), wallMaterial.clone());
            wall.position.copy(localPos);
            wall.rotation.y = localYaw;
            wall.castShadow = true;
            wall.receiveShadow = true;

            if (withWindow) {
                const windowPanel = new THREE.Mesh(new THREE.BoxGeometry(1.4, 1.2, 0.06), new THREE.MeshStandardMaterial({ color: 0x5c7188 }));
                windowPanel.position.set(0, 0.55, 0.17);
                wall.add(windowPanel);
            }

            if (withDoor) {
                const doorPanel = new THREE.Mesh(new THREE.BoxGeometry(1.2, 2.0, 0.08), frameMaterial.clone());
                doorPanel.position.set(0, -0.7, 0.18);
                wall.add(doorPanel);
            }

            this.mesh.add(wall);

            const localOutward = localPos.clone();
            localOutward.y = 0;
            if (localOutward.lengthSq() < 0.001) {
                localOutward.set(0, 0, 1);
            }
            localOutward.normalize();

            const worldOutward = localOutward.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), this.rotation);
            const launch = worldOutward.multiplyScalar(0.18 + Math.random() * 0.2);
            launch.addScaledVector(this.#explosionDirection, 0.08 + Math.random() * 0.12);
            launch.y = 0.16 + Math.random() * 0.18;

            this.#debrisPieces.push({
                mesh: wall,
                type: 'wall',
                velocity: launch,
                angularVelocity: new THREE.Vector3(
                    (Math.random() - 0.5) * 0.08,
                    (Math.random() - 0.5) * 0.04,
                    (Math.random() - 0.5) * 0.08
                ),
                landed: false,
            });
        };

        createWallPiece(new THREE.Vector3(0, 2.8, 3.9), 0, true, true);
        createWallPiece(new THREE.Vector3(0, 2.8, -3.9), Math.PI, false, true);
        createWallPiece(new THREE.Vector3(4.9, 2.8, 0), Math.PI / 2, false, true);
        createWallPiece(new THREE.Vector3(-4.9, 2.8, 0), -Math.PI / 2, false, true);

        const roof = new THREE.Mesh(
            new THREE.ConeGeometry(4.8, 2.8, 4),
            new THREE.MeshStandardMaterial({ color: 0x3d2410, roughness: 0.85 })
        );
        roof.position.set(0, 7.5, 0);
        roof.rotation.y = Math.PI / 4;
        roof.castShadow = true;
        roof.receiveShadow = true;
        this.mesh.add(roof);

        this.#debrisPieces.push({
            mesh: roof,
            type: 'roof',
            velocity: new THREE.Vector3(
                this.#explosionDirection.x * (0.2 + Math.random() * 0.22),
                0.22 + Math.random() * 0.2,
                this.#explosionDirection.z * (0.2 + Math.random() * 0.22)
            ),
            angularVelocity: new THREE.Vector3(
                (Math.random() - 0.5) * 0.1,
                (Math.random() - 0.5) * 0.06,
                (Math.random() - 0.5) * 0.1
            ),
            landed: false,
        });
    }

    /**
     * @param {THREE.Vector3} localPos
     * @returns {THREE.Vector3}
     */
    #localToWorldPosition(localPos) {
        return localPos.clone()
            .applyAxisAngle(new THREE.Vector3(0, 1, 0), this.rotation)
            .add(this.position);
    }

    /**
     * @param {THREE.Vector3} worldPos
     * @returns {THREE.Vector3}
     */
    #worldToLocalPosition(worldPos) {
        return worldPos.clone()
            .sub(this.position)
            .applyAxisAngle(new THREE.Vector3(0, 1, 0), -this.rotation);
    }

    /**
     * @param {THREE.Vector3} worldVec
     * @returns {THREE.Vector3}
     */
    #worldToLocalDirection(worldVec) {
        return worldVec.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), -this.rotation);
    }

    /**
     * @param {DebrisPiece} piece
     * @returns {number}
     */
    #getDebrisCollisionRadius(piece) {
        return piece.type === 'roof' ? 3.9 : 2.5;
    }

    /**
     * @param {DebrisPiece} piece
     * @param {number} groundY
     */
    #landDebrisPiece(piece, groundY) {
        piece.velocity.set(0, 0, 0);
        piece.angularVelocity.set(0, 0, 0);
        piece.landed = true;

        if (piece.type === 'wall') {
            piece.mesh.rotation.x = Math.PI / 2;
            piece.mesh.rotation.z = 0;
        } else {
            const upright = Math.random() < 0.5;
            piece.mesh.rotation.x = upright ? 0 : Math.PI;
            piece.mesh.rotation.y = Math.round(piece.mesh.rotation.y / (Math.PI / 2)) * (Math.PI / 2);
            piece.mesh.rotation.z = 0;
        }

        const bounds = new THREE.Box3().setFromObject(piece.mesh);
        piece.mesh.position.y += (groundY - bounds.min.y);

        piece.settleFrames = 3;
        piece.settleBasePosition = piece.mesh.position.clone();
        piece.settleBaseRotation = piece.mesh.rotation.clone();

        if (piece.type === 'wall') {
            const wallWorldPos = new THREE.Vector3();
            piece.mesh.getWorldPosition(wallWorldPos);
            this.#wallLandingPositions.push(wallWorldPos);
        } else {
            const roofWorldPos = new THREE.Vector3();
            piece.mesh.getWorldPosition(roofWorldPos);
            this.#roofLandingPosition = roofWorldPos;
        }
    }

    /**
     * Apply a tiny damped post-impact settle motion for a few frames.
     * @param {DebrisPiece} piece
     */
    #updateLandedDebrisSettle(piece) {
        if (!piece.settleFrames || !piece.settleBasePosition || !piece.settleBaseRotation) {
            return;
        }

        const frame = piece.settleFrames;
        const decay = frame / 3;
        const bounceY = Math.sin(frame * Math.PI * 0.6) * 0.03 * decay;
        const tilt = 0.03 * decay;

        piece.mesh.position.copy(piece.settleBasePosition);
        piece.mesh.position.y += Math.abs(bounceY);

        piece.mesh.rotation.copy(piece.settleBaseRotation);
        if (piece.type === 'wall') {
            piece.mesh.rotation.x += (Math.random() - 0.5) * tilt;
            piece.mesh.rotation.z += (Math.random() - 0.5) * tilt;
        } else {
            piece.mesh.rotation.y += (Math.random() - 0.5) * tilt;
        }

        piece.settleFrames -= 1;
        if (piece.settleFrames <= 0) {
            piece.mesh.position.copy(piece.settleBasePosition);
            piece.mesh.rotation.copy(piece.settleBaseRotation);
            piece.settleBasePosition = undefined;
            piece.settleBaseRotation = undefined;
            piece.settleFrames = 0;
        }
    }

    /**
     * @param {DebrisPiece} piece
     * @param {THREE.Vector3} obstaclePos
     * @param {number} obstacleRadius
     */
    #bounceDebrisOffObstacle(piece, obstaclePos, obstacleRadius) {
        const worldPos = this.#localToWorldPosition(piece.mesh.position);
        const radius = this.#getDebrisCollisionRadius(piece);
        const delta = worldPos.clone().sub(obstaclePos);
        delta.y = 0;
        const distSq = delta.lengthSq();
        const minDist = obstacleRadius + radius;

        if (distSq >= minDist * minDist) {
            return;
        }

        let normal = delta;
        if (normal.lengthSq() < 0.0001) {
            normal = this.#explosionDirection.clone();
        }
        normal.normalize();

        const correctedWorld = obstaclePos.clone().addScaledVector(normal, minDist + 0.02);
        piece.mesh.position.copy(this.#worldToLocalPosition(correctedWorld));

        const normalComponent = normal.clone().multiplyScalar(piece.velocity.dot(normal));
        const tangentialComponent = piece.velocity.clone().sub(normalComponent);
        piece.velocity.copy(tangentialComponent.multiplyScalar(0.72).sub(normalComponent.multiplyScalar(0.52)));
        piece.velocity.y *= 0.76;
    }

    /**
     * @param {number} delta
     * @param {House[]} houses
     * @param {import('./Tree.js').Tree[]} trees
     */
    #updateDebrisPhysics(delta, houses, trees) {
        const gravity = 0.0085;
        const floorY = 0.02;

        this.#debrisPieces.forEach((piece) => {
            if (piece.landed) {
                this.#updateLandedDebrisSettle(piece);
                return;
            }

            piece.velocity.y -= gravity * delta * 60;
            const worldMovement = piece.velocity.clone().multiplyScalar(delta * 60);
            piece.mesh.position.add(this.#worldToLocalDirection(worldMovement));

            piece.mesh.rotation.x += piece.angularVelocity.x * delta * 60;
            piece.mesh.rotation.y += piece.angularVelocity.y * delta * 60;
            piece.mesh.rotation.z += piece.angularVelocity.z * delta * 60;

            for (const tree of trees) {
                this.#bounceDebrisOffObstacle(piece, tree.position, tree.collisionRadius + 0.6);
            }

            for (const house of houses) {
                if (house === this || house.collidable === false) {
                    continue;
                }

                this.#bounceDebrisOffObstacle(piece, house.position, House.COLLISION_RADIUS + 0.6);
            }

            const bounds = new THREE.Box3().setFromObject(piece.mesh);
            const centerY = bounds.getCenter(new THREE.Vector3()).y;
            if (bounds.min.y <= floorY && piece.velocity.y <= 0 && centerY <= 2.4) {
                this.#landDebrisPiece(piece, floorY);
            }
        });
    }

    /**
     * Get the doorstep's world position
     * @returns {THREE.Vector3}
     */
    getDoorstepWorldPosition() {
        const worldPos = new THREE.Vector3();
        this.#doorstep.getWorldPosition(worldPos);
        return worldPos;
    }

    /**
     * Mark the doorstep as pooped and add a floating poop emoji sprite
     * @param {THREE.Texture} poopTexture - The poop emoji texture
     * @returns {boolean} True if this was a fresh poop, false if already pooped
     */
    poopOnDoorstep(poopTexture) {
        if (this.#doorstepPooped) return false;

        this.#doorstepPooped = true;

        // Create a hovering poop emoji sprite over the house
        const spriteMaterial = new THREE.SpriteMaterial({
            map: poopTexture,
            transparent: true,
        });
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.scale.set(5, 5, 1);
        sprite.position.set(0, 13, 0);
        this.mesh.add(sprite);
        this.#poopSprite = sprite;

        return true;
    }

    /**
     * Get the poop sprite (if any)
     * @returns {THREE.Sprite|null}
     */
    get poopSprite() {
        return this.#poopSprite;
    }

    /**
     * Build the full house mesh
     * @param {number} houseColor - Hex color for the walls
     */
    #buildMesh(houseColor) {
        // House base
        const baseGeometry = new THREE.BoxGeometry(House.WIDTH, 6, House.DEPTH);
        const baseMaterial = new THREE.MeshStandardMaterial({ color: houseColor });
        const base = new THREE.Mesh(baseGeometry, baseMaterial);
        base.position.y = 3;
        base.castShadow = true;
        base.receiveShadow = true;
        this.mesh.add(base);
        this.#baseMesh = base;

        // Roof
        const roofGeometry = new THREE.ConeGeometry(7.5, 4, 4);
        const roofMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
        const roof = new THREE.Mesh(roofGeometry, roofMaterial);
        roof.position.y = 8;
        roof.rotation.y = Math.PI / 4;
        roof.castShadow = false;
        roof.receiveShadow = true;
        this.mesh.add(roof);
        this.#roofMesh = roof;

        // Door
        const doorGeometry = new THREE.BoxGeometry(2, 3, 0.2);
        const doorMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
        const door = new THREE.Mesh(doorGeometry, doorMaterial);
        door.position.set(0, 1.5, 4.1);
        door.castShadow = true;
        door.receiveShadow = true;
        this.mesh.add(door);
        this.#doorMesh = door;

        // Doorstep
        const doorstepGeometry = new THREE.BoxGeometry(3, 0.2, 2);
        const doorstepMaterial = new THREE.MeshStandardMaterial({ color: 0xA9A9A9 });
        this.#doorstep = new THREE.Mesh(doorstepGeometry, doorstepMaterial);
        this.#doorstep.position.set(0, 0.1, 5.1);
        this.#doorstep.castShadow = true;
        this.#doorstep.receiveShadow = true;
        this.mesh.add(this.#doorstep);

        // Windows
        this.#addWindows();

        // Garden with flowers
        this.#addGarden();
    }

    /**
     * Add windows to the house
     */
    #addWindows() {
        const windowGeometry = new THREE.BoxGeometry(1.5, 1.5, 0.1);
        const windowMaterial = new THREE.MeshStandardMaterial({ color: 0xADD8E6 });

        // Front windows
        const frontWindow1 = new THREE.Mesh(windowGeometry, windowMaterial);
        frontWindow1.position.set(-2.5, 3, 4.05);
        frontWindow1.castShadow = true;
        frontWindow1.receiveShadow = true;
        this.mesh.add(frontWindow1);
        this.#windowMeshes.push(frontWindow1);

        const frontWindow2 = new THREE.Mesh(windowGeometry, windowMaterial);
        frontWindow2.position.set(2.5, 3, 4.05);
        frontWindow2.castShadow = true;
        frontWindow2.receiveShadow = true;
        this.mesh.add(frontWindow2);
        this.#windowMeshes.push(frontWindow2);

        // Side windows
        const sideWindow1 = new THREE.Mesh(windowGeometry, windowMaterial);
        sideWindow1.position.set(5.05, 3, 0);
        sideWindow1.rotation.y = Math.PI / 2;
        sideWindow1.castShadow = true;
        sideWindow1.receiveShadow = true;
        this.mesh.add(sideWindow1);
        this.#windowMeshes.push(sideWindow1);

        const sideWindow2 = new THREE.Mesh(windowGeometry, windowMaterial);
        sideWindow2.position.set(-5.05, 3, 0);
        sideWindow2.rotation.y = Math.PI / 2;
        sideWindow2.castShadow = true;
        sideWindow2.receiveShadow = true;
        this.mesh.add(sideWindow2);
        this.#windowMeshes.push(sideWindow2);
    }

    /**
     * Add a garden with flowers around the house
     */
    #addGarden() {
        const gardenDepth = 0.05;
        const gardenSize = 14;
        const gardenGeometry = new THREE.BoxGeometry(gardenSize, gardenDepth, gardenSize);
        const gardenMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
        const garden = new THREE.Mesh(gardenGeometry, gardenMaterial);
        garden.position.y = gardenDepth / 2;
        garden.receiveShadow = true;
        this.mesh.add(garden);
        this.#gardenMesh = garden;

        // Add flowers
        for (let i = 0; i < 30; i++) {
            let flowerX, flowerZ;
            do {
                flowerX = (Math.random() - 0.5) * (gardenSize - 1);
                flowerZ = (Math.random() - 0.5) * (gardenSize - 1);
            } while (
                (Math.abs(flowerX) < 5 && Math.abs(flowerZ) < 4) ||
                (Math.abs(flowerX) < 1.5 && Math.abs(flowerZ - 5.1) < 1)
            );

            const flowerGroup = new THREE.Group();
            flowerGroup.position.set(flowerX, 0, flowerZ);
            garden.add(flowerGroup);
            this.#flowers.push(flowerGroup);

            // Stem
            const stemHeight = 0.3 + Math.random() * 0.2;
            const stemGeometry = new THREE.CylinderGeometry(0.02, 0.02, stemHeight, 8);
            const stemMaterial = new THREE.MeshStandardMaterial({ color: 0x00FF00 });
            const stem = new THREE.Mesh(stemGeometry, stemMaterial);
            stem.position.y = stemHeight / 2;
            stem.castShadow = true;
            stem.receiveShadow = true;
            flowerGroup.add(stem);

            // Flower head
            const flowerGeometry = new THREE.SphereGeometry(0.2, 8, 8);
            const flowerColor = FLOWER_COLORS[Math.floor(Math.random() * FLOWER_COLORS.length)];
            const flowerMaterial = new THREE.MeshStandardMaterial({ color: flowerColor });
            const flower = new THREE.Mesh(flowerGeometry, flowerMaterial);
            flower.position.y = stemHeight + 0.1;
            flower.castShadow = true;
            flower.receiveShadow = true;
            flowerGroup.add(flower);
        }
    }
}
